/**
 * Component: SafeHtml
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R09, BROWSE-V1-R08
 *
 * Shared component that sanitises HTML content via DOMPurify before rendering.
 * Used for guideline content fields (RecommendationText, SectionText,
 * ExplanatoryText, IndicationText) that contain clinically important formatting
 * from the guideline database.
 *
 * Allow-list preserves:
 * - Text structure: p, br, h1-h6, blockquote
 * - Inline formatting: strong, b, em, i, sup, sub, u, span
 * - Anchors: a (BROWSE-V1-R08 inline citation references)
 * - Lists: ul, ol, li
 * - Tables: table, thead, tbody, tr, th, td
 * - Figures: figure, figcaption, img (src attribute for guideline algorithm diagrams)
 *
 * Data attributes: data-reference-id (BROWSE-V1-R08) so inline citation
 * anchors can carry the upstream reference id into the viewer's popover
 * resolver without inventing a new href scheme.
 *
 * Strips: script, iframe, style, event handlers (onclick, onerror, etc.)
 *
 * Bug #3884: Replaces stripHtml() for rendered content contexts.
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  // Text structure
  'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote',
  // Inline formatting
  'strong', 'b', 'em', 'i', 'sup', 'sub', 'u', 'span',
  // Anchors (BROWSE-V1-R08 inline citation references)
  'a',
  // Lists
  'ul', 'ol', 'li',
  // Tables
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  // Figures (guideline algorithm diagrams)
  'figure', 'figcaption', 'img',
];

const ALLOWED_ATTR = [
  'src', 'alt', 'class', 'href', 'target', 'rel',
  // BROWSE-V1-R08: carry the reference id on inline citation
  // anchors so SectionContent can resolve + open the popover
  // without inventing a new href scheme.
  'data-reference-id',
  // BROWSE-V1-R08: upstream uses data-toggle="popover" with
  // data-content for inline citation references.
  'data-content', 'data-toggle', 'data-placement',
];

function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

interface SafeHtmlProps {
  html: string | null | undefined;
  className?: string;
  as?: 'div' | 'p' | 'span';
}

export function SafeHtml({ html, className, as: Tag = 'div' }: SafeHtmlProps): React.ReactElement | null {
  if (!html) return null;
  const clean = sanitizeHtml(html);
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
}
